/**
 * Módulo de galería personal — upload, listado, descarga, edición, eliminación y vinculación de fotos
 */

/**
 * Crea el módulo de galería
 *
 * @param {Object} deps - Dependencias inyectadas
 * @param {Function} deps.authenticatedRequest - Función de request autenticada
 * @param {Function} deps.authenticatedFormDataRequest - Función para uploads y descargas blob
 * @returns {Object} Métodos del módulo galería
 */
export function createGaleriaModule({ authenticatedRequest, authenticatedFormDataRequest }) {
  return {
    /**
     * Sube una foto a la galería personal
     *
     * @param {FormData} formData - Debe contener: archivo, descripcion (opcional)
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async uploadFoto(formData) {
      return authenticatedFormDataRequest('/api/galeria', {
        method: 'POST',
        body: formData
      });
    },

    /**
     * Obtiene fotos propias del usuario (paginado)
     *
     * @param {Object} [params] - Parámetros de consulta
     * @param {number} [params.page=1] - Número de página
     * @param {number} [params.limit=20] - Fotos por página
     * @returns {Promise<{success: boolean, data: Array, pagination: Object}>}
     */
    async getMisFotos(params = {}) {
      const query = new URLSearchParams(params).toString();
      return authenticatedRequest(`/api/galeria${query ? `?${query}` : ''}`);
    },

    /**
     * Descarga imagen completa para fullscreen
     *
     * @param {number} id - ID de la foto
     * @returns {Promise<Response>}
     */
    async downloadFoto(id) {
      return authenticatedFormDataRequest(`/api/galeria/${id}/download`, {
        responseType: 'blob'
      });
    },

    /**
     * Descarga thumbnail 400px para grid
     *
     * @param {number} id - ID de la foto
     * @returns {Promise<Response>}
     */
    async downloadThumbnail(id) {
      return authenticatedFormDataRequest(`/api/galeria/${id}/thumbnail`, {
        responseType: 'blob'
      });
    },

    /**
     * Actualiza descripción de una foto propia
     *
     * @param {number} id - ID de la foto
     * @param {string} descripcion - Nueva descripción
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async updateFotoDescripcion(id, descripcion) {
      return authenticatedRequest(`/api/galeria/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ descripcion })
      });
    },

    /**
     * Elimina una foto propia (hard delete + storage)
     *
     * @param {number} id - ID de la foto
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async deleteFoto(id) {
      return authenticatedRequest(`/api/galeria/${id}`, {
        method: 'DELETE'
      });
    },

    /**
     * Transcribe un blob de audio a texto usando Groq Whisper
     *
     * @param {FormData} formData - Debe contener campo "audio" con el blob grabado
     * @returns {Promise<{success: boolean, text: string}>}
     */
    async transcribirAudio(formData) {
      return authenticatedFormDataRequest('/api/transcribir', {
        method: 'POST',
        body: formData
      });
    },

  };
}
